<script setup lang="ts">
import { computed } from "vue";
import { cssUrl } from "../cssUrl";

/**
 * Lucide glyph rendered as a CSS mask so it inherits currentColor.
 *
 * The handoff fetched glyphs from a CDN. We resolve them from vendored SVGs
 * instead, so the app keeps working offline and under a strict CSP.
 */
const props = withDefaults(
  defineProps<{
    /** Lucide slug, e.g. "flame", "droplet", "bike". */
    name: string;
    /** Square px size. */
    size?: number;
    /** Override colour; defaults to currentColor. */
    color?: string;
  }>(),
  { size: 20 },
);

const glyphs = import.meta.glob("../assets/icons/*.svg", {
  eager: true,
  query: "?url",
  import: "default",
});

const byName: Record<string, string> = Object.fromEntries(
  Object.entries(glyphs).map(([path, url]) => [
    path.split("/").pop()!.replace(/\.svg$/, ""),
    url,
  ]),
);

const url = computed(() => byName[props.name]);

const style = computed(() => {
  const u = url.value;
  if (!u) {
    return {
      width: `${props.size}px`,
      height: `${props.size}px`,
      backgroundColor: props.color ?? "currentColor",
    };
  }
  const mask = cssUrl(u);
  return {
    width: `${props.size}px`,
    height: `${props.size}px`,
    backgroundColor: props.color ?? "currentColor",
    WebkitMaskImage: mask,
    maskImage: mask,
  };
});
</script>

<template>
  <span
    v-if="url"
    class="cw-icon"
    role="img"
    aria-hidden="true"
    :data-icon="name"
    :style="style"
  />
</template>
